import { useEffect, useRef } from 'react';
import type { OrderDetail } from '../api';

interface OrderDetailsModalProps {
  order: OrderDetail | null;
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  error?: string | null;
}

function formatCurrency(amount: number | string, currency = 'USD') {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(num);
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getStatusColor(status: string) {
  switch (status) {
    case 'verified':
      return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
    case 'unverified':
      return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    case 'needs_review':
      return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    case 'unattributed':
      return 'bg-ink-700/50 text-ink-400 border-ink-600';
    default:
      return 'bg-ink-700/50 text-ink-400 border-ink-600';
  }
}

export default function OrderDetailsModal({
  order,
  isOpen,
  onClose,
  isLoading,
  error,
}: OrderDetailsModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Focus trap - focus close button on open
      closeButtonRef.current?.focus();
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Handle click outside
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-950/80 backdrop-blur-sm animate-fade-in"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-modal-title"
    >
      <div
        ref={modalRef}
        className="w-full max-w-lg bg-ink-900 rounded-xl border border-ink-800 shadow-2xl animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-ink-800">
          <h2 id="order-modal-title" className="text-lg font-semibold text-ink-50">
            Order Details
          </h2>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="p-2 rounded-lg text-ink-400 hover:text-ink-200 hover:bg-ink-800 transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-4 rounded-lg bg-accent-500/10 border border-accent-500/20 text-accent-400">
              {error}
            </div>
          ) : order ? (
            <div className="space-y-4">
              {/* Order ID and Date */}
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-ink-500 uppercase tracking-wider mb-1">Order ID</p>
                  <p className="font-mono text-lg text-ink-100">#{order.shopify_order_id}</p>
                </div>
                <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusColor(order.attribution_status)}`}>
                  {order.attribution_status.replace('_', ' ')}
                </span>
              </div>

              {/* Date */}
              <div>
                <p className="text-xs text-ink-500 uppercase tracking-wider mb-1">Date</p>
                <p className="text-ink-200">{formatDate(order.created_at)}</p>
              </div>

              {/* Amount */}
              <div className="p-4 rounded-lg bg-ink-800/50 border border-ink-700/50">
                <p className="text-xs text-ink-500 uppercase tracking-wider mb-1">Total Amount</p>
                <p className="text-2xl font-display font-bold text-accent-400">
                  {formatCurrency(order.total_price, order.currency)}
                </p>
              </div>

              {/* Customer Name */}
              {(order.customer_first_name || order.customer_last_name) && (
                <div>
                  <p className="text-xs text-ink-500 uppercase tracking-wider mb-1">Customer</p>
                  <p className="text-ink-200">
                    {[order.customer_first_name, order.customer_last_name].filter(Boolean).join(' ')}
                  </p>
                </div>
              )}

              {/* Location */}
              <div className="grid grid-cols-2 gap-4">
                {order.order_state && (
                  <div>
                    <p className="text-xs text-ink-500 uppercase tracking-wider mb-1">State/Province</p>
                    <p className="text-ink-200">{order.order_state}</p>
                  </div>
                )}
                {order.order_country && (
                  <div>
                    <p className="text-xs text-ink-500 uppercase tracking-wider mb-1">Country</p>
                    <p className="text-ink-200">{order.order_country}</p>
                  </div>
                )}
              </div>

              {/* Attribution Info (collapsible/debug) */}
              <details className="group">
                <summary className="cursor-pointer text-xs text-ink-500 uppercase tracking-wider hover:text-ink-400 transition-colors">
                  Attribution Details
                  <span className="ml-1 group-open:rotate-90 inline-block transition-transform">▶</span>
                </summary>
                <div className="mt-2 p-3 rounded-lg bg-ink-800/30 text-sm space-y-2">
                  <div className="flex justify-between">
                    <span className="text-ink-500">Source:</span>
                    <span className="text-ink-300 font-mono text-xs">{order.attributed_source}</span>
                  </div>
                  {order.storefront_id && (
                    <div className="flex justify-between">
                      <span className="text-ink-500">Storefront ID:</span>
                      <span className="text-ink-300 font-mono text-xs">{order.storefront_id}</span>
                    </div>
                  )}
                  {order.influencer_id && (
                    <div className="flex justify-between">
                      <span className="text-ink-500">Influencer ID:</span>
                      <span className="text-ink-300 font-mono text-xs">{order.influencer_id}</span>
                    </div>
                  )}
                  {order.slug && (
                    <div className="flex justify-between">
                      <span className="text-ink-500">Slug:</span>
                      <span className="text-ink-300 font-mono text-xs">{order.slug}</span>
                    </div>
                  )}
                </div>
              </details>
            </div>
          ) : (
            <div className="text-center py-8 text-ink-500">
              No order data available
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-ink-800">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-ink-800 hover:bg-ink-700 text-ink-200 rounded-lg transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
